import React, { useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useQueryClient } from 'react-query';
import { Grid, Button, CircularProgress } from '@nextiva/next-ui-components';
import { dataSelector } from '@nextiva/global-store/dist/selectors/globalStoreSelectors';
import { actionTypes } from 'containers/pages/Teams/redux';

import { createHuntGroup } from 'api/huntGroupApi';
import { SvgVoice, SvgTeamSuccess } from 'components/SvgIcon';
import { TEAMS } from 'constants/routes';
import { Page } from 'containers';
import { USER_UPDATE } from 'containers/pages/ManageUsers/duck/types';
import { useSnackbar } from 'hooks';
import { findLicenseFeature } from 'utils/license-helpers';

const ThankYou = ({ pageTitle, newlyCreatedTeam }) => {
  const [breadCrumbs, setBreadCrumbs] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const setSnackbar = useSnackbar();
  const globalStoreData = useSelector(dataSelector);
  const queryClient = useQueryClient();
  const isTeam = pageTitle.includes('Team');

  const voiceEnabled = () => {
    findLicenseFeature(globalStoreData.licenses, 'PLATFORM_VOICE_ACCESS');
  };

  const routeToTeamsPage = () => {
    history.push(TEAMS);
  };

  const routeToVoiceSettings = () => {
    setIsLoading(true);

    createHuntGroup(newlyCreatedTeam)
      .then((res) => {
        dispatch({
          type: actionTypes.SET_EDIT_TEAM_INFO,
          editTeamInfo: newlyCreatedTeam,
        });
        history.push(
          `/teams/call-group/${res.huntGroupId}/${res.callingLineIdLastName}/phone-number`,
        );
        queryClient.invalidateQueries(['getTeams']);
      })
      .catch(() => {
        const errorText = newlyCreatedTeam?.name
          ? `team ${newlyCreatedTeam.name}`
          : 'this team';

        setSnackbar({
          message: `There was a problem creating the voice settings for ${errorText}. Please try again later or contact support if the issue persists.`,
          severity: 'error',
        });
        setIsLoading(false);
      });
  };

  const BreadCrumbs = () => {
    const pathname = pageTitle.split('/');
    const crumbs = (
      <>
        {pathname.map((path, i, arr) => {
          if (i === 0) {
            return (
              <Link
                key={path}
                to={path.toLowerCase()}
                data-testid="PlatformAdminUI-ThankYou-Link-0"
              >
                {path}
              </Link>
            );
          }

          if (i + 1 === arr.length) return ` / ${path}`;

          const link = arr
            .slice(0, i + 1)
            .join('/')
            .toLowerCase();

          return (
            <Link
              key={path}
              to={link}
              data-testid={`PlatformAdminUI-ThankYou-Link-${i}`}
            >{` / ${path}`}</Link>
          );
        })}
      </>
    );

    setBreadCrumbs(crumbs);
  };

  useEffect(() => {
    BreadCrumbs();
  }, [pageTitle]);

  const onVoiceSetupHandler = (e) => {
    e.stopPropagation();
    // redirect to voice setup with profile id of newly created user
    const [, paramString] = window.location.hash.split('?');
    const [, profileId] = paramString
      .split('&')
      .find((p) => p.includes('profileId'))
      .split('=');
    const redirectUrl = `${window.location.origin}/apps/voice-admin/#/users/${profileId}/setup`;

    window.location = redirectUrl;
  };

  const onDoneHandler = (e) => {
    e.stopPropagation();
    dispatch({ type: USER_UPDATE, payload: true });
    // on done go back to users page
    window.location.hash = '#/users';
  };

  return (
    <Page titleComponent={breadCrumbs}>
      <div className="m-thank-you">
        {isTeam ? <SvgTeamSuccess /> : <SvgVoice />}
        <div className="m-thank-you__message">
          Congrats! You&#39;ve successfully created a {isTeam ? 'team' : 'user'}
          .
        </div>
        {(voiceEnabled || isTeam) && (
          <div className="m-thank-you__direction">
            Continue to set up voice for this {isTeam ? 'team' : 'user'}, or
            click done and add more.
          </div>
        )}
        <div className="m-thank-you__actions">
          <Grid container direction="row" spacing={2} wrap="wrap">
            <Grid item>
              {(voiceEnabled || isTeam) && (
                <Button
                  onClick={isTeam ? routeToVoiceSettings : onVoiceSetupHandler}
                  color="primary"
                  variant="contained"
                  data-testid="PlatformAdminUI-ThankYou-Continue"
                  disabled={isLoading}
                >
                  Continue to voice {isTeam ? 'settings' : 'set up'}
                  {isLoading && (
                    <CircularProgress
                      style={{ color: '#ffffff', marginLeft: 14 }}
                      mode="indeterminate"
                      size={14}
                    />
                  )}
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button
                onClick={isTeam ? routeToTeamsPage : onDoneHandler}
                color="secondary"
                variant="contained"
                disabled={isLoading}
                data-testid="PlatformAdminUI-ThankYou-Done"
              >
                Done
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>
    </Page>
  );
};

ThankYou.propTypes = {
  pageTitle: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => {
  return {
    newlyCreatedTeam: state.teamsReduxReducer.newlyCreatedTeam,
  };
};

export default connect(mapStateToProps)(ThankYou);
